import { useEffect, useState } from 'react';
import { Button } from './Button.tsx';

export interface CascadeItem {
  label: string;
  count: number;
  sublabel?: string;
}

export interface DetachItem {
  label: string;
  sublabel?: string;
}

interface Props {
  title: string;
  intro: string;
  willBeRemoved: CascadeItem[];
  willBeDetached?: DetachItem[];
  warnings?: string[];
  confirmName?: string;
  confirmButtonLabel?: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

// Generic destructive-action confirm modal used for org, project, and
// ticket deletes. The two-column "What gets removed / What stays"
// listing is the core of the contract: the operator sees every
// cascading row count before clicking Delete, so nothing is destroyed
// silently. Pass `confirmName` for the org variant — the Delete button
// stays disabled until the user re-types the exact string.
export function DeleteCascadeDialog({
  title,
  intro,
  willBeRemoved,
  willBeDetached,
  warnings,
  confirmName,
  confirmButtonLabel = 'Delete',
  onConfirm,
  onClose,
}: Props): React.ReactElement {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const nameRequired = confirmName !== undefined;
  const nameOk = !nameRequired || typed.trim() === confirmName;

  async function handleConfirm() {
    setError(null);
    setBusy(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl rounded-[var(--radius-card)] bg-white shadow-xl">
        <div className="border-b border-[var(--color-border)] px-6 py-4">
          <h2 className="text-lg font-bold text-[var(--color-danger)]">{title}</h2>
        </div>

        <div className="space-y-4 px-6 py-5">
          <p className="text-sm text-[var(--color-body)]">{intro}</p>

          {warnings && warnings.length > 0 && (
            <ul className="space-y-2 rounded-[var(--radius-button)] border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              {warnings.map((w, i) => (
                <li key={i}>⚠️ {w}</li>
              ))}
            </ul>
          )}

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-danger)]">
              Will be permanently deleted
            </h3>
            {willBeRemoved.length === 0 ? (
              <p className="mt-2 text-sm text-[var(--color-muted)]">
                No dependent records — only this record itself.
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-[var(--color-border)] rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)]">
                {willBeRemoved.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <div>
                      <div className="text-[var(--color-body)]">{item.label}</div>
                      {item.sublabel && (
                        <div className="text-xs text-[var(--color-muted)]">{item.sublabel}</div>
                      )}
                    </div>
                    <div className="font-mono font-semibold text-[var(--color-header)]">
                      {item.count.toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {willBeDetached && willBeDetached.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-header)]">
                Will be kept, but unlinked
              </h3>
              <ul className="mt-2 divide-y divide-[var(--color-border)] rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white">
                {willBeDetached.map((item, i) => (
                  <li key={i} className="px-3 py-2 text-sm">
                    <div className="text-[var(--color-body)]">{item.label}</div>
                    {item.sublabel && (
                      <div className="text-xs text-[var(--color-muted)]">{item.sublabel}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {nameRequired && (
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Type <code className="rounded bg-[var(--color-surface)] px-1.5 py-0.5 font-mono text-xs">{confirmName}</code> to confirm
              </span>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                disabled={busy}
                className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 font-mono text-sm outline-none focus:border-[var(--color-danger)] focus:ring-2 focus:ring-[var(--color-danger)]/30"
                placeholder={confirmName}
              />
            </label>
          )}

          {error && (
            <p className="text-sm text-[var(--color-danger)]" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleConfirm()}
            disabled={busy || !nameOk}
          >
            {busy ? 'Deleting…' : confirmButtonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
